// src/Components/DPDPAssessmentModule.jsx
import React, { useState, useContext, useEffect } from "react";
import {
  Form,
  ProgressBar,
  Button,
  Card,
  Row,
  Col,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Menubar from "../Menubar";
import BackgroundFooter from "../BackgroundFooter";
import NavigationBar from "../Nav";
import { ScoreContext } from "../ScoreContext";
import generateDPDPCertificate from "./generateDPDPCertificate";
import susanLogo from "../../Assets/srql.png";
import html2canvas from "html2canvas";
import CertificateCanvas from "./CertificateCanvas";

// ✅ DPDP QUESTIONS - inline define kar diya (alag file ki zaroorat nahi)
 const dpdpQuestions = [
  {
    questionId: 1,
    question: "DPDP Act applies to which data?",
    options: [
      { value: "A", label: "Only paper records" },
      { value: "B", label: "Digital personal data or data digitised subsequently" },
      { value: "C", label: "Only biometric data" },
      { value: "D", label: "Only government data" },
    ],
    correctAnswer: "B",
    description:
      "DPDP Act 2023 covers all digital personal data and personal data that is later converted into digital form.",
    recommendations:
      "Map all digital and digitised personal data in your systems to ensure compliance with DPDP obligations.",
  },
  {
    questionId: 2,
    question: "Who is a Data Principal under DPDP?",
    options: [ 
      { value: "A", label: "Data processor" },
      { value: "B", label: "Indian citizen only" },
      { value: "C", label: "Company collecting data" },
      { value: "D", label: "Individual to whom personal data relates" },
    ],
    correctAnswer: "D",
    description:
      "A Data Principal is the natural person whose personal data is being processed under the Act.",
    recommendations:
      "Design rights-handling processes with the Data Principal at the centre, not the organisation.",
  },
  {
    questionId: 3,
    question: "Consent under DPDP must be ?",
    options: [
      { value: "A", label: "Implied and general" },
      { value: "B", label: "Free, specific, informed, unconditional and unambiguous" },
      { value: "C", label: "Written only" },
      { value: "D", label: "Permanent" },
    ],
    correctAnswer: "B",
    description:
      "DPDP requires valid consent to be free from coercion, clearly explained and specific to the stated purposes.",
    recommendations:
      "Use clear language, separate consent from T&Cs and allow easy withdrawal of consent.",
  },
  {
    questionId: 4,
    question: "Can consent be bundled with service access?",
    options: [
      { value: "A", label: "No, consent must be freely given" },
      { value: "B", label: "Only for premium services" },
      { value: "C", label: "Yes, always" },
      { value: "D", label: "If disclosed in policy" },
    ],
    correctAnswer: "A",
    description:
      "Bundled or forced consent is not considered freely given under DPDP.",
    recommendations:
      "Avoid making non‑essential data processing a condition for accessing core services.",
  },
  {
    questionId: 5,
    question: "Which is NOT a lawful basis under DPDP?",
    options: [
      { value: "A", label: "Consent" },
      { value: "B", label: "Deemed consent" },
      { value: "C", label: "Legitimate interest (as in GDPR)" },
      { value: "D", label: "Legal obligation" },
    ],
    correctAnswer: "C",
    description:
      "Unlike GDPR, DPDP does not recognise legitimate interest as a separate lawful basis.",
    recommendations:
      "Classify processing under consent, deemed consent or legal/other obligations instead of legitimate interest.",
  },
  {
    questionId: 6,
    question: "What triggers extraterritorial applicability of DPDP?",
    options: [
      { value: "A", label: "Offering goods or services to individuals in India" },
      { value: "B", label: "Indian employees" },
      { value: "C", label: "Servers in India" },
      { value: "D", label: "Indian shareholders" },
    ],
    correctAnswer: "A",
    description:
      "DPDP applies to entities outside India when they process personal data in connection with offering goods or services to individuals in India.",
    recommendations:
      "Non‑Indian entities targeting Indian users should implement DPDP controls even if processing occurs abroad.",
  },
  {
    questionId: 7,
    question:
      "A hospital in India treats foreign nationals. Does DPDP apply to their personal data?",
    options: [
      { value: "A", label: "Only to Indian citizens" },
      { value: "B", label: "Yes, processing occurs in India" },
      { value: "C", label: "Only with written consent" },
      { value: "D", label: "No, healthcare is exempt" },
    ],
    correctAnswer: "B",
    description:
      "DPDP focuses on location of processing, so data processed in India is covered regardless of nationality.",
    recommendations:
      "Apply the same DPDP safeguards to both Indian and foreign patients in Indian facilities.",
  },
  {
    questionId: 8,
    question: "Which right allows correction of inaccurate data under DPDP?",
    options: [
      { value: "A", label: "Right to access" },
      { value: "B", label: "Right to grievance" },
      { value: "C", label: "Right to correction and erasure" },
      { value: "D", label: "Right to portability" },
    ],
    correctAnswer: "C",
    description:
      "Data Principals can require correction, completion, updating and erasure of their personal data in many cases.",
    recommendations:
      "Provide simple channels for users to request corrections and deletions of their records.",
  },
  {
    questionId: 9,
    question: "Does DPDP provide a right to data portability?",
    options: [
      { value: "A", label: "Yes" },
      { value: "B", label: "Yes, for Significant Data Fiduciaries" },
      { value: "C", label: "Only for banks" },
      { value: "D", label: "No" },
    ],
    correctAnswer: "D",
    description:
      "Unlike GDPR, DPDP does not create an explicit data portability right for Data Principals.",
    recommendations:
      "If portability is offered, treat it as a voluntary feature rather than a statutory obligation.",
  },
  {
    questionId: 10,
    question: "What happens if personal data is anonymised under DPDP?",
    options: [
      { value: "A", label: "Outside scope of DPDP" },
      { value: "B", label: "Needs erasure on request" },
      { value: "C", label: "Still covered under DPDP" },
      { value: "D", label: "Needs consent" },
    ],
    correctAnswer: "A",
    description:
      "Truly anonymised data falls outside the scope of DPDP as it is no longer personal data.",
    recommendations:
      "Use strong anonymisation techniques; once data is anonymised, DPDP obligations generally cease.",
  },
  {
    questionId: 11,
    question: "Who must appoint a Data Protection Officer (DPO) under DPDP?", 
    options: [
      { value: "A", label: "All companies" },
      { value: "B", label: "Significant Data Fiduciaries" },
      { value: "C", label: "Data processors" },
      { value: "D", label: "Startups only" },
    ],
    correctAnswer: "B",
    description:
      "Only entities classified as Significant Data Fiduciaries are required to appoint a DPO.",
    recommendations:
      "Evaluate if your organisation may become an SDF and plan DPO resourcing accordingly.",
  },
  {
    questionId: 12,
    question: "Can a DPO also act as Consent Manager under DPDP?",
    options: [
      { value: "A", label: "Yes, always" },  
      { value: "B", label: "Only with board approval" },
      { value: "C", label: "No, Consent Manager must be independent" },
      { value: "D", label: "Only for SMEs" },
    ],
    correctAnswer: "C",
    description:
      "Consent Managers are meant to be independent, interoperable entities distinct from the DPO role.",
    recommendations:
      "Separate compliance oversight (DPO) functions from consent management platforms and vendors.",
  },
  {
    questionId: 13,
    question: "What factors determine Significant Data Fiduciary status?",
    options: [
      { value: "A", label: "Revenue" },
      { value: "B", label: "Industry" },
      { value: "C", label: "Number of employees" },
      { value: "D", label: "Volume and sensitivity of personal data, risk to individuals" },
    ],
    correctAnswer: "D",
    description:
      "SDF classification depends on how much and what type of personal data is processed and the resulting risk to Data Principals.",
    recommendations:
      "Monitor data volumes and sensitivity to anticipate possible SDF designation and extra obligations.",
  },
  {
    questionId: 14,
    question: "Who can file a complaint under DPDP?",
    options: [
      { value: "A", label: "Only courts" },
      { value: "B", label: "Data Principal" },
      { value: "C", label: "Police" },
      { value: "D", label: "Auditor" },
    ],
    correctAnswer: "B",
    description:
      "Data Principals can bring complaints regarding violations of their rights or obligations under the Act.",
    recommendations:
      "Set up a clear grievance redressal mechanism to handle complaints before they reach the DPB.",
  },
  {
    questionId: 15,
    question: "Can the Data Protection Board (DPB) act without receiving a complaint?",
    options: [
      { value: "A", label: "No" },
      { value: "B", label: "Only with court order" },
      { value: "C", label: "Yes, suo motu" },
      { value: "D", label: "Only against government" },
    ],
    correctAnswer: "C",
    description:
      "The DPB has power to initiate proceedings on its own (suo motu) in appropriate cases.",
    recommendations:
      "Serious non‑compliance may attract regulatory attention even without a direct user complaint.",
  },
  {
    questionId: 16,
    question: "What is the nature of penalties under DPDP?",
    options: [
      { value: "A", label: "Criminal imprisonment" },
      { value: "B", label: "Civil damages" },
      { value: "C", label: "Administrative monetary penalties" },
      { value: "D", label: "Contractual fines" },
    ],
    correctAnswer: "C",
    description:
      "DPDP relies on substantial administrative monetary penalties imposed by the DPB, not criminal jail terms.",
    recommendations:
      "Treat DPDP as a regulatory regime with financial consequences similar to competition or telecom regulators.",
  },
  {
    questionId: 17,
    question: "What is the maximum penalty for breach of security safeguards under DPDP?",
    options: [
      { value: "A", label: "₹50 crore" },
      { value: "B", label: "₹100 crore" },
      { value: "C", label: "₹200 crore" },
      { value: "D", label: "Up to ₹250 crore" },
    ],
    correctAnswer: "D",
    description:
      "Serious failure to implement reasonable security safeguards can attract penalties up to ₹250 crore.",
    recommendations:
      "Invest in strong technical and organisational security measures to avoid large penalty exposure.",
  },
  {
    questionId: 18,
    question: "What is the role of a Consent Manager under DPDP?",
    options: [ 
      { value: "A", label: "Data processing" },
      { value: "B", label: "Compliance audits" },
      { value: "C", label: "Manage, review, withdraw consent on behalf of Data Principal" },
      { value: "D", label: "Data storage" },
    ],
    correctAnswer: "C",
    description:
      "Consent Managers are entities that help Data Principals give, manage and withdraw consent in a standardised way.",
    recommendations:
      "Integrate with Consent Managers where required to provide users central control over their permissions.",
  },
  {
    questionId: 19,
    question: "A company changes the purpose of data use. What is required under DPDP?",
    options: [
      { value: "A", label: "Policy update" },
      { value: "B", label: "Notice only" },
      { value: "C", label: "Fresh consent" },
      { value: "D", label: "Board approval" },
    ],
    correctAnswer: "C",
    description:
      "Using personal data for a new purpose generally requires fresh, specific consent.",
    recommendations:
      "Avoid purpose creep; if business needs change, collect new consent before re‑using data.",
  },
  {
    questionId: 20,
    question:
      "A job portal retains candidate data for 5 years for future roles without consent. Is this allowed?",
    options: [
      { value: "A", label: "No, violates purpose limitation and retention" },
      { value: "B", label: "Only if disclosed" },
      { value: "C", label: "Yes" },
      { value: "D", label: "Only for successful candidates" },
    ],
    correctAnswer: "A",
    description:
      "Retaining data beyond the original purpose without consent breaches purpose limitation and storage limitation.",
    recommendations:
      "Define clear retention periods and obtain explicit consent for long‑term talent pools.",
  },
  {
    questionId: 21,
    question: "Differential Privacy adds what to data or outputs?",
    options: [
      { value: "A", label: "Encryption" },
      { value: "B", label: "Access controls" },
      { value: "C", label: "Carefully calibrated noise to prevent re-identification" },
      { value: "D", label: "Watermarks" },
    ],
    correctAnswer: "C",
    description:
      "Differential privacy techniques inject controlled statistical noise to protect individual records while keeping aggregates useful.",
    recommendations:
      "Consider differential privacy for analytics on sensitive datasets to reduce re‑identification risk.",
  },
  {
  questionId: 22,
  question: "Under the DPDP Act, what is required before an organization processes personal data?",
  options: [
    { value: "A", label: "Obtaining lawful and informed consent from the individual" },
    { value: "B", label: "Approval from senior management" },
    { value: "C", label: "Completion of internal audit checks" },
    { value: "D", label: "Notification to external vendors" },
  ],
  correctAnswer: "A",
  description:
    "The DPDP Act requires organizations to obtain lawful, informed consent from individuals before processing their personal data, except in specified legitimate use cases.",
  recommendations:
    "Ensure consent mechanisms are clear, specific, and documented before collecting or processing personal data.",
},
  {
    questionId: 23,
    question: "When is consent required for AI use under DPDP?",
    options: [
      { value: "A", label: "Always" },
      { value: "B", label: "For non-essential uses like marketing or profiling" },
      { value: "C", label: "Never" },
      { value: "D", label: "Only biometric AI" },
    ],
    correctAnswer: "B",
    description:
      "AI that profiles individuals or uses their data for non‑essential marketing needs valid consent.",
    recommendations:
      "Treat AI‑driven marketing, recommendations and profiling as consent‑based processing with opt‑out options.",
  },
  {
    questionId: 24,
    question: "Who will certify data auditors under DPDP?",
    options: [
      { value: "A", label: "ISO" },
      { value: "B", label: "CERT-In" },
      { value: "C", label: "To be notified by the Government / DPB" },
      { value: "D", label: "ICAI only" },
    ],
    correctAnswer: "C",
    description:
      "The Government or Data Protection Board will notify the mechanism and bodies for certifying data auditors.",
    recommendations:
      "Track future notifications on approved auditors before engaging them for mandatory audits.",
  },
  {
    questionId: 25,
    question:
      "Failure to implement reasonable security safeguards under DPDP leads to what consequence?",
    options: [
      { value: "A", label: "Criminal prosecution" },
      { value: "B", label: "Warning only" },
      { value: "C", label: "Business shutdown" },
      { value: "D", label: "Monetary penalty imposed by DPB" },
    ],
    correctAnswer: "D",
    description:
      "Inadequate security safeguards can result in significant monetary penalties from the DPB.",
    recommendations:
      "Conduct regular risk assessments and harden controls to demonstrate reasonable security measures.",
  },
];





 const btnPrimary = {
  padding: "7px 16px", borderRadius: "8px", fontSize: "12px", fontWeight: 600,
   cursor: "pointer", border: "none",backgroundColor:"#024A82",color:"#FFFFF",
   fontFamily:"'Inter', sans-serif",
   display: "inline-flex",
  alignItems: "center", gap: "5px",
};



// ========== BACKEND ENDPOINT FUNCTIONS  ==========
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';  // added to .env 

// 1. ✅ Fetch User Profile ( use for NAME)
const fetchUserProfile = async (username) => {
  try {
    const response = await fetch(`${API_BASE}/users/${username}/profile`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,  // Cognito token
        'Content-Type': 'application/json'
      }
    });
    return await response.json();
  } catch (error) {
    console.error('Profile fetch failed:', error);
    return { name: username || 'Participant', email: '' };
  }
};

// 2. ✅ Save Assessment Responses
const saveAllResponsesToBackend = async (username, section, responses) => {
  try {
    const score = responses.reduce((acc, r) => acc + (r.isCorrect ? 1 : 0), 0);
    const percentage = Math.round((score / 25) * 100);
    
    await fetch(`${API_BASE}/assessments/${username}/${section}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ responses, score, percentage })
    });
    
    console.log('✅ Assessment saved:', { username, percentage });
  } catch (error) {
    console.error('Save failed:', error);
  }
};

// 3. ✅ Fetch Previous Responses
const fetchResponsesForSection = async (username, section) => {
  try {
    const response = await fetch(`${API_BASE}/assessments/${username}/${section}`, {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
    const data = await response.json();
    return data.responses || [];
  } catch (error) {
    console.error('Fetch failed:', error);
    return [];
  }
};

// 4. ✅ Clear Responses
const clearResponsesForSection = async (username, section) => {
  try {
    await fetch(`${API_BASE}/assessments/${username}/${section}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
    });
  } catch (error) {
    console.error('Clear failed:', error);
  }
};

// 5. ✅ Generate Certificate (Name + Backend)
const generateDPDPCertificateWithBackend = async (userData, assessmentData) => {
  const username = userData?.cognito?.username;
  if (!username) return;
  
  // Backend se profile + assessment fetch
  const [profile, assessment] = await Promise.all([ 
    fetchUserProfile(username),
    fetch(`${API_BASE}/assessments/${username}/DPDPAssessment`)
      .then(r => r.json())
  ]);
  
  const fullData = {
    ...assessmentData,
    userName: profile.name,
    certificateId: `DPDP-${Date.now()}-${Math.random().toString(36).substr(2,6).toUpperCase()}`
  };
  
  // your jsPDF call
  generateDPDPCertificate({ ...userData, name: profile.name }, fullData, susanLogo);
};


// Function to download the PDF guide

const DPDPPdf ="https://d2i158pkl9gun1.cloudfront.net/DPDP%20Training%20Guide.pdf";

const handleDownloadPDFGuide = () => {
  const link = document.createElement('a');
  link.href = DPDPPdf; 
  // link.download = 'DPDP_Training_Guide.pdf'
   link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};




// ✅ Simple shuffle function
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};






const DPDPAssessmentModule = ({ userData }) => {
  const { fetchResponsesForSection, updateResponses, saveAllResponsesToBackend, updateScore, clearResponsesForSection } = useContext(ScoreContext);
  const navigate = useNavigate();
  const section = "DPDPAssessment";

  const SIDEBAR_EXPANDED_WIDTH = 317;
  const SIDEBAR_COLLAPSED_WIDTH = 90;

const [shuffledQuestions, setShuffledQuestions] = useState([]);

const [showRulesModal, setShowRulesModal] = useState(false);

 

  

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(JSON.parse(localStorage.getItem('isSidebarCollapsed')) || false);
  const [currentStep, setCurrentStep] = useState(0); // 0=intro, 1=questions
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [comments, setComments] = useState({});
  const [loadingComments, setLoadingComments] = useState(false);
  const [activeTab, setActiveTab] = useState('desc');
  const [currentSlide, setCurrentSlide] = useState(0);




  const [showCertificateModal, setShowCertificateModal] = useState(false);
const [certificatePreviewUrl, setCertificatePreviewUrl] = useState(null);

const [showCertificateCanvas, setShowCertificateCanvas] = useState(false);






































  const handleSidebarToggle = () => setIsSidebarCollapsed(prev => !prev);

  useEffect(() => {
    localStorage.setItem('isSidebarCollapsed', JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

  useEffect(() => {
    if (userData?.cognito?.username) {
      clearResponsesForSection(section);
      fetchResponsesForSection(userData.cognito.username, section);
    }
  }, [userData, fetchResponsesForSection, clearResponsesForSection]);
    const [activeSection, setActiveSection] = useState({});
  

const handleStartAssessment = () => {

  
  setShowRulesModal(true);  
};
const handleSkipVideo = () => {
  setCurrentSlide(1); // Go to first slide
}; 
const handleSkipTraining = () => {
  setCurrentStep(1); 
};


const handleBackToIntro = () => {
  setShowResults(false);
  setCurrentStep(0);  
  setCurrentSlide(0); 
  setCurrentQuestionIndex(0);
  setSelectedAnswers({});
};
   

const [showLinkedInModal, setShowLinkedInModal] = useState(false);



// Function to show certificate preview
const handleDownloadCertificate = () => {
  const previewUrl = generateDPDPCertificate(
    userData,
    { percentage, score, totalQuestions },
    susanLogo,
    true // previewMode = true
  );
  setCertificatePreviewUrl(previewUrl);
  setShowCertificateModal(true);
};

// Function to actually download PDF
const handleActualDownload = () => {
  generateDPDPCertificate(
    userData,
    { percentage, score, totalQuestions },
    susanLogo,
    false, // previewMode = false
    'pdf' // export as PDF
  );
};

const handleShareLinkedIn = () => {
  setShowCertificateModal(false);
  setShowLinkedInModal(true);
};


const downloadCertificateAsPNG = async () => {
  try {
    console.log('🎨 Starting PNG certificate generation...');
    
    // Show hidden canvas
    setShowCertificateCanvas(true);
    
    // Wait for render
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const canvas = document.getElementById('certificate-canvas');
    if (!canvas) {
      throw new Error('Certificate canvas not found');
    }
    
    // Generate PNG using html2canvas
    const generatedCanvas = await html2canvas(canvas, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#f8fcf8',
      logging: false
    });
    
    // Convert to blob
    generatedCanvas.toBlob((blob) => {
      if (blob) {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const userName = userData?.name || userData?.['cognito:username'] || 'Participant';
        const currentDate = new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
        
        link.href = url;
        link.download = `DPDP_Certificate_${userName.replace(/\s+/g, "_")}_${currentDate.replace(/\s+/g, "_")}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        console.log('✅ PNG Certificate downloaded successfully!');
        
        // Hide canvas after download
        setShowCertificateCanvas(false);
      }
    }, 'image/png', 1.0);
    
  } catch (error) {
    console.error('❌ PNG generation failed:', error);
    alert('Failed to generate PNG certificate. Please try again.');
    setShowCertificateCanvas(false);
  }
};









































  const handleAnswerSelect = (answer) => {
const q = shuffledQuestions[currentQuestionIndex];
    const isCorrect = answer === q.correctAnswer;
    const value = isCorrect ? 1 : 0;

    setSelectedAnswers(prev => ({
      ...prev,
      [currentQuestionIndex]: answer
    }));

    updateResponses(section, q.questionId, {
      questionId: q.questionId,
      answer,
      isCorrect,  
      questionText: q.question
    });

    updateScore(section, q.questionId, value, q.question, q.correctAnswer);
    if (userData?.cognito?.username) {
      saveAllResponsesToBackend(userData.cognito.username, section);
    }
  };

  const handleNext = () => {
   if (currentQuestionIndex < shuffledQuestions.length - 1)
 {
      setCurrentQuestionIndex(i => i + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(i => i - 1);
    }
  };



//   const handleNextSlide = () => {
//   if (currentSlide < dpdpSlides.length) {
//     setCurrentSlide(prev => prev + 1);
//   }
// };



const handlePrevSlide = () => {
  if (currentSlide > 1) {
    setCurrentSlide(prev => prev - 1);
  } else if (currentSlide === 1) {
    setCurrentSlide(0); // Back to video
  }
};


 

  const sidebarWidth = isSidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_EXPANDED_WIDTH;
const totalQuestions = shuffledQuestions.length;
  const answeredQuestions = Object.keys(selectedAnswers).length;
  const progress = totalQuestions ? Math.round((answeredQuestions / totalQuestions) * 100) : 0;
 const score = Object.entries(selectedAnswers).reduce((acc, [idx, ans]) => {
  const q = shuffledQuestions[Number(idx)];   // ✅ FIXED
  return acc + (q && String(ans) === String(q.correctAnswer) ? 1 : 0);
}, 0);

  const percentage = totalQuestions ? Math.round((score / totalQuestions) * 100) : 0;
const currentQuestion = shuffledQuestions[currentQuestionIndex];

const videoUrl = "https://d2ev5p5ld91dj6.cloudfront.net/DPDP%20Training.mov";
  

  return (
    <div>
      <Row className="g-0">
        {/* Sidebar */}
 <Col
                 xs={12}
                 className={`bg-transparent sidebar-container ${isSidebarCollapsed ? "collapsed" : "hello"}`}
                 style={{
                   width: isSidebarCollapsed ? "90px" : "317px",
                   transition: "width 0.3s ease",
                 }} 
               >
                 <Menubar
                   isCollapsed={isSidebarCollapsed}
                   onToggle={handleSidebarToggle}
                 />
      </Col>

        {/* Main content */}
        <Col className="pt-0 px-0 ps-0 section-container" style={{ width: `calc(100% - ${sidebarWidth}px)`, transition: 'width 0.3s ease' }}>
          <NavigationBar userData={userData} isSidebarCollapsed={isSidebarCollapsed} />

          {/* Progress bar */}
          {currentStep !== 0 && (
            <div className="sticky-top bg-white" style={{ top: '50px', zIndex: 999 }}>
              <div style={{ position: 'relative' }}>
                <ProgressBar now={progress} className="custom-progress-bar" style={{ height: '20px', margin: '0 10px 10px 10px' }} />
                <span style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', color: '#000' }}>
                  {progress}% completed
                </span>
              </div>
            </div>
          )}

          <Col className="overflow-auto p-4">
           {/* INTRO SCREEN - UPDATED */}
{currentStep === 0 && (
  <Row className="justify-content-center">
    <Col xs={12}>
      <Card className="iam-card iam-card-intro text-center">
        <Card.Body>
          <h2>DPDP Act 2023 – Q&A Module</h2>
          <p>Understand India's Digital Personal Data Protection Act through training and 25-question assessment.</p>
          

          {/* STEP 1: VIDEO SCREEN */}
           {currentSlide === 0 && (
            <div> 
              {/* Video placeholder */} 
               <div className="mb-4">
                
                { videoUrl ? (
                  <video src={videoUrl} controls   controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: '68%' }} />
                ) : (
                  <div style={{
                    width: '100%', height: '420px', borderRadius: '10px', border: '2px dashed #999',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8f9fa',
                    flexDirection: 'column', gap: '1rem'
                  }}>
                    <div style={{ fontSize: '1.2rem', color: '#666' }}>
                       DPDP Training Video
                    </div>
                    <div style={{ color: '#999', fontSize: '0.9rem' }}>
                      Video placeholder – add video source here
                    </div>
                  </div>
                )}
              </div> 
              
  <div className="d-flex justify-content-center gap-3 mt-3">
  <Button variant="" onClick={handleStartAssessment} 
  style={{height:"50px"}}
  className='bg-secondary text-white'
  >
    Skip Video
  </Button>
   
  <div className="d-flex justify-content-center  "
    style={{marginTop:"-15px"}}
    >
     <Button variant="primary" onClick={handleStartAssessment} style={{
      height:"50px" , marginTop:"15px",...btnPrimary
      
     }}>
   Done - Start Assessment
</Button>

 <Button
    variant="success"
    onClick={handleDownloadPDFGuide}
    style={{
      height:"50px" , marginTop:"15px", marginLeft:"15px"     
      }}>
     Download PDF Guide
  </Button>


{/* Rules Confirmation Modal */}
<Modal show={showRulesModal} onHide={() => setShowRulesModal(false)} centered size="lg">
  <Modal.Header closeButton style={{ borderBottom: 'none' }}>
    <Modal.Title>DPDP Assessment Rules</Modal.Title>
  </Modal.Header>
  <Modal.Body>
    <ul>
      <li>25 multiple-choice questions on DPDP Act 2023</li>
      <li>Each question has 4 options, only 1 correct</li>
      <li>1 point per correct answer (no negative marking)</li>  
      <li>Certificate on 75%+ score</li>
    </ul>

    {/* NEW SECTION - Certificate Validity */}
    <div style={{ 
      marginTop: '20px', 
      padding: '15px', 
      backgroundColor: '#f8f9fa', 
      borderRadius: '8px',
      border: '1px solid #dee2e6'
    }}>
      <h6 style={{ fontWeight: 'bold', marginBottom: '10px' }}>Certificate Validity:</h6>
      <p style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: 0 }}>
        This certification is based on the DPDP Act, 2023 and its provisions as applicable through 
        January 2026. If the DPDP Act is amended or updated thereafter, certificate holders may be 
        required to undergo a revision or re-certification. The certificate reflects demonstrated 
        knowledge of practical compliance requirements and AI governance principles and may be shared 
        on LinkedIn, included in your CV, or displayed in your workplace.
      </p>
    </div>

    <div style={{ 
      backgroundColor: '#fff3cd', 
      padding: '12px', 
      borderRadius: '5px', 
      marginTop: '20px', 
      border: '1px solid #ffc107'
    }}>
      <strong style={{ color: '#856404' }}>Important:</strong>
      <span style={{ color: '#856404', marginLeft: '8px' }}> 
        Attempt honestly. No retakes without clearing responses.
      </span>
    </div>
  </Modal.Body>
  <Modal.Footer style={{ borderTop: 'none', justifyContent: 'flex-end' }}>
    <Button variant="secondary" onClick={() => setShowRulesModal(false)}>
      Cancel
    </Button>
    <Button 
      variant="success" 
      onClick={() => {
        setShowRulesModal(false);
        const shuffled = shuffleArray(dpdpQuestions);
        setShuffledQuestions(shuffled);
        setCurrentStep(1);
      }}
    >
      I Agree & Start Exam
    </Button>
  </Modal.Footer>
</Modal>

    </div>
</div>


            </div>
          )} 

         
{/* STEP 2: COMPLETE DPDP TRAINING GUIDE - FULL PDF CONTENT */}
{/* {currentSlide === 1 && (
  <div> */}
    {/* Complete Scrollable Training Content */}
    {/* <div
      className="training-content-scrollable"
      style={{
        maxHeight: "55vh",
        overflowY: "auto",
        border: "2px solid #dee2e6",
        borderRadius: "12px",
        padding: "24px",
        background: "#f8f9fa",
        marginBottom: "24px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
        fontSize: "0.95rem",
        lineHeight: "1.6",
    }}
    >
      <div className="training-module-content"> */}

        {/* ================= MODULE 1 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    📋 <strong>Module 1: Introduction to DPDP Act</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-primary">
    <h6 className="fw-bold  mb-2">Overview:</h6>
    <p className="mb-3">
      India's Digital Personal Data Protection Act, 2023 is the
      foundational legislation governing data protection in India. This Act
      applies to the processing of digital personal data and
      covers individuals referred to as Data Principals. The
      primary objective of the Act is to ensure{" "}
      lawful, fair, and transparentprocessing of personal data
      while protecting the rights and interests of individuals whose data is
      being processed.
    </p>

    <h6 className="fw-bold  mb-2">Key Points:</h6>
    <ul className="mb-0">
      <li>
        <strong>Applies to:</strong> Digital personal data processing
      </li>
      <li>
        <strong>Covers:</strong> Individuals called Data Principals
      </li>
      <li>
        <strong>Objective:</strong> Lawful, fair, and transparent processing
      </li>
    </ul>
  </div>
</section> */}


       {/* ================= MODULE 2 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
     
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🌍 <strong>Module 2: Scope & Applicability</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-success">
    <h6 className="fw-bold  mb-2">Coverage:</h6>
    <p className="mb-3">
      The DPDP Act applies comprehensively to all processing
      activities conducted in India. The legislation has{" "}
      extraterritorial applicability, meaning it extends beyond
      India’s borders to cover any processing undertaken outside India that
      specifically targets Indian residents and Data Principals.
    </p>

    <h6 className="fw-bold  mb-2">Important Exclusion:</h6>
    <p className="mb-3">
      However, it is important to note that the Act does{" "}
      not apply to anonymised data, which is data that cannot be
      linked to or used to identify any individual.
    </p>

    <h6 className="fw-bold mb-2">Key Points:</h6>
    <ul className="mb-0">
      <li>
        <strong>Applies to:</strong> Processing activities in India
      </li>
      <li>
        <strong>Extraterritorial:</strong> Indians targeted abroad
      </li>
      <li>
        <strong>Exclusion:</strong> Anonymised data
      </li>
    </ul>
  </div>
</section>
 */}

        {/* ================= MODULE 3 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    👥 <strong>Module 3: Key Definitions & Roles</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-purple">

    <h6 className="fw-bold  mb-2">Data Principal:</h6>
    <p className="mb-3">
      Refers to any individual to whom the personal data relates.
      This is the person whose information is being processed.
    </p>

    <h6 className="fw-bold  mb-2">Data Fiduciary:</h6>
    <p className="mb-3">
      The entity that determines the purpose and means of
      processing personal data. The Data Fiduciary makes decisions about{" "}
     what data to collect, how to use it, and for what purposes.
    </p>

    <h6 className="fw-bold  mb-2">Data Processor:</h6>
    <p className="mb-3">
      An entity that processes personal data on behalf of the Data Fiduciary.
      They act under the instructions of the Data Fiduciary and
      handle the technical or operational aspects of data processing.
    </p>

    <h6 className="fw-bold  mb-2">Consent Manager:</h6>
    <p className="mb-0">
      An independent and interoperable entityresponsible for
      managing consent on behalf of Data Principals. They serve as intermediaries
      ensuring that consent is properly obtained and tracked.
    </p>

  </div>
</section>
 */}

      {/* ================= MODULE 4 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    ✅ <strong>Module 4: Consent Framework</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-danger">

    <h6 className="fw-bold  mb-2">
      Requirements for Valid Consent:
    </h6>
    <p className="mb-3">
      Consent under the DPDP Act must meet strict criteria to be
      valid. Consent must be free, meaning it cannot be obtained
      through coercion, undue influence, or as a condition for receiving
      unrelated services. Consent must be specific, indicating
      that it applies to particular purposes rather than broad or general
      categories.
    </p>

    <h6 className="fw-bold mb-2">
      Informed &amp; Affirmative:
    </h6>
    <p className="mb-3">
      Consent must be informed, requiring that individuals
      understand what they are consenting to and how their data will be used.
      Clear affirmative action is required, meaning individuals
      must take an active step to provide consent.{" "}
      Pre-ticked boxes or opt-out mechanisms are not acceptable.
    </p>

    <h6 className="fw-bold  mb-2">
      Granular &amp; Withdrawable:
    </h6>
    <p className="mb-0">
      The consent framework mandates that consent be{" "}
      granular and purpose-specific, allowing individuals to
      consent to different uses separately. Importantly, individuals retain the
      right to withdraw consent at any time, and once withdrawn,
      the Data Fiduciary must cease processing that personal
      data.
    </p>

  </div>
</section> */}

        {/* ================= MODULE 5 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🏛️ <strong>Module 5: Deemed Consent</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-secondary">

    <h6 className="fw-bold  mb-2">State Functions:</h6>
    <p className="mb-3">
      Certain situations allow for processing without explicit consent through
      the concept of deemed consent. State functions fall under
      deemed consent when data processing is necessary to{" "}
      fulfil legal obligations imposed on the State.
    </p>

    <h6 className="fw-bold  mb-2">Employment Purposes:</h6>
    <p className="mb-3">
      Employment purposes are covered under deemed consent when data processing
      is required as part of employment relationships and
      compliance with labour laws.
    </p>

    <h6 className="fw-bold  mb-2">Medical Emergencies:</h6>
    <p className="mb-0">
      Medical emergencies represent another category where deemed consent
      applies, allowing immediate data processing to protect the
       life or health of an individual in critical situations.
    </p>

  </div>
</section> */}

        {/* ================= MODULE 6 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    ⚖️ <strong>Module 6: Rights of Data Principal</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-warning">

    <h6 className="fw-bold  mb-2">Access &amp; Correction:</h6>
    <p className="mb-3">
      The DPDP Act grants Data Principals several important
      rights. The right to access allows individuals to obtain
      information about what personal data is being held about them and how it
      is being processed. The right to correction and erasure
      enables individuals to correct inaccurate data or have their data deleted
      when it is no longer necessary.
    </p>

    <h6 className="fw-bold  mb-2">Grievance &amp; Nomination:</h6>
    <p className="mb-3">
      The right to grievance redressal is explicitly mentioned
      in Section 13 of the Act, providing a mechanism for
      individuals to seek redressal if their rights are violated. The{" "}
      right to nominate allows Data Principals to designate
      another person to exercise their rights in case of{" "}
      death or incapacity.
    </p>

    <h6 className="fw-bold  mb-2">No Data Portability:</h6>
    <p className="mb-0">
      However, notably, the DPDP Act does not include a data portability
      right, distinguishing it from other global data protection
      frameworks.
    </p>

  </div>
</section> */}


        {/* ================= MODULE 7 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🛡️ <strong>Module 7: Obligations of Data Fiduciary</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-info">

    <h6 className="fw-bold  mb-2">
      Purpose Limitation &amp; Minimisation:
    </h6>
    <p className="mb-3">
      Data Fiduciaries must adhere to several key obligations.{" "}
      Purpose limitation requires that personal data be used
      only for the purposes for which it was collected and{" "}
      explicitly communicated to the Data Principal.{" "}
      Data minimisation mandates that only the{" "}
      minimum amount of personal data necessary to achieve the
      stated purpose should be collected and processed.
    </p>

    <h6 className="fw-bold  mb-2">
      Security &amp; Breach Notification:
    </h6>
    <p className="mb-0">
      Data Fiduciaries must implement accuracy and security
      safeguards to ensure that personal data is accurate, up-to-date,
      and protected from unauthorised access or breach.
      Additionally, Fiduciaries must establish and maintain{" "}
      breach notification procedures, ensuring that Data
      Principals are informed promptly if their personal data is
      compromised.
    </p>

  </div>
</section> */}


        {/* ================= MODULE 8 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🏢 <strong>Module 8: Significant Data Fiduciary</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-primary">

    <h6 className="fw-bold  mb-2">
      Definition &amp; Criteria:
    </h6>
    <p className="mb-3">
      The concept of a Significant Data Fiduciary is based on the
       volume of personal data processed and the{" "}
      sensitivity of that data, creating a risk to Data
      Principals. Organisations determined to be Significant Data Fiduciaries
      face enhanced compliance obligations.
    </p>

    <h6 className="fw-bold  mb-2">
      Mandatory Requirements:
    </h6>
    <p className="mb-0">
      These organisations must appoint a{" "}
     Data Protection Officer (DPO) who is responsible for
      ensuring compliance with the Act. Additionally, Significant Data
      Fiduciaries are required to conduct{" "}
      independent data audits to assess their compliance posture
      and identify potential risks or violations.
    </p>

  </div>
</section> */}


        {/* ================= MODULE 9 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🗄️ <strong>Module 9: Data Retention &amp; Deletion</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-danger">

    <h6 className="fw-bold  mb-2"> 
      Retention Principle:
    </h6>
    <p className="mb-3">
      A fundamental principle of data protection is that{" "} 
      personal data should be retained only as long as necessary{" "}
      to fulfil the purpose for which it was collected. Once the purpose is
      fulfilled, the data must be deleted.
    </p>

    <h6 className="fw-bold mb-2">
      Consent Withdrawal:
    </h6>
    <p className="mb-3">
      Ifconsent is withdrawn, the Data Fiduciary must delete the
      associated personal data without delay.
    </p>

    <h6 className="fw-bold  mb-2">
      Future Use:
    </h6>
    <p className="mb-0">
      Furthermore, personal data{" "}
      cannot be retained for potential future use unless the
      Data Principal provides{" "}
      fresh consent specifically for that new purpose.
    </p>

  </div>
</section> */}


       {/* ================= MODULE 10 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🌐 <strong>Module 10: Cross-Border Data Transfer</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-success">

    <h6 className="fw-bold  mb-2">
      Default Permission:
    </h6>
    <p className="mb-3">
      The DPDP Act permits{" "}
     cross-border data transfers by default, meaning
      organisations can transfer personal data outside India{" "}
      without requiring specific authorisation.
    </p>

    <h6 className="fw-bold  mb-2">
      Government Restrictions:
    </h6>
    <p className="mb-3">
      However, the Government of India may issue notifications
      identifying countries or territories that are restricted
      for data transfers due to{" "}
      insufficient data protection standards.
    </p>

    <h6 className="fw-bold  mb-2">
      No Data Localisation Mandate:
    </h6>
    <p className="mb-0">
      It is important to note that the Act{" "}
     does not impose a data localisation mandate, allowing
      businesses flexibility in their data infrastructure decisions while
      maintaining the right to restrict transfers when necessary.
    </p>

  </div>
</section> */}

     {/* ================= MODULE 11 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    📝 <strong>Module 11: Grievance Redressal</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-secondary">

    <h6 className="fw-bold  mb-2">
      First Step – Data Fiduciary:
    </h6>
    <p className="mb-3">
      The grievance redressal mechanism follows a{" "}
     structured approach. The first step requires individuals
      to approach the Data Fiduciary directly to resolve their
      concerns.
    </p>

    <h6 className="fw-bold  mb-2">
      Escalation to DPB:
    </h6>
    <p className="mb-3">
      If the Data Fiduciary fails to address the grievance{" "}
     satisfactorily, individuals may{" "}
      escalate the matter to the{" "}
      Data Protection Board (DPB).
    </p>

    <h6 className="fw-bold  mb-2">
      Suo Motu Powers:
    </h6>
    <p className="mb-0">
      The DPB is empowered to act{" "}
      suo motu, meaning it can{" "}
      initiate investigations and proceedings on its own
      initiative even without a formal complaint from an individual.
    </p>

  </div>
</section> */}


       {/* ================= MODULE 12 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    💰 <strong>Module 12: Enforcement &amp; Penalties</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-danger">

    <h6 className="fw-bold  mb-2">
      Administrative Framework:
    </h6>
    <p className="mb-3">
      The DPDP Act establishes an enforcement framework based on{" "}
      administrative penalties rather than criminal prosecution.
      There is no criminal imprisonment provision in the Act,
      distinguishing it from traditional criminal law frameworks.
    </p>

    <h6 className="fw-bold mb-2">
      Penalty Structure:
    </h6>
    <p className="mb-0">
      Penalties are imposed by the{" "}
      Data Protection Board (DPB) and are{" "}
      monetary in nature, scaled according to the{" "}
     severity of the violation. The quantum of penalties varies
      based on the seriousness of the breach, allowing for{" "}
     proportionate enforcement.
    </p>

  </div>
</section> */}


       {/* ================= MODULE 13 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🤖 <strong>Module 13: AI &amp; Emerging Technology</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-teal">

    <h6 className="fw-bold  mb-2">
      Purpose-Based Regulation:
    </h6>
    <p className="mb-3">
      The DPDP Act takes a{" "}
      purpose-based approach to regulating{" "}
      artificial intelligence rather than regulating the
      technology itself. This means that the{" "}
      legality of AI systems depends on whether their{" "}
      intended use is lawful under the Act, not on the
      technology’s characteristics.
    </p>

    <h6 className="fw-bold  mb-2">
      Consent for Marketing &amp; Profiling:
    </h6>
    <p className="mb-3">
      Specific consent is required when personal data is used
      for marketing or profiling purposes, even if the data was
      originally collected for different purposes.
    </p>

    <h6 className="fw-bold  mb-2">
      Differential Privacy:
    </h6>
    <p className="mb-0">
     Differential privacy, an advanced privacy protection
      technique that adds calibrated noise to datasets, is
      recognised as a valid mechanism for protecting individual
      privacy while still enabling data analysis.
    </p>

  </div>
</section> */}


        {/* ================= MODULE 14 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    📚 <strong>Module 14: DPDP vs GDPR (Exam Focus)</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-warning">

    <h6 className="fw-bold  mb-2">
      Key Differences:
    </h6>
    <p className="mb-3">
      Understanding the distinctions between the{" "}
      DPDP Act and the{" "}
      European GDPR is important for comprehensive knowledge.
      The DPDP Act does{" "}
      not include the concept of legitimate interest as a lawful
      basis for processing, unlike GDPR.
    </p>

    <h6 className="fw-bold  mb-2">
      Rights &amp; Data:
    </h6>
    <p className="mb-3">
      The Act does{" "}
      not grant data portability rights to individuals, a
      significant right under GDPR.{" "}
     Special category data does not exist as a concept in the
      DPDP Act, meaning all personal data is treated with the
      same level of protection.
    </p>

    <h6 className="fw-bold  mb-2">
      Remedies:
    </h6>
    <p className="mb-0">
      Additionally, the DPDP Act{" "}
     does not provide for compensation to individuals for
      violations, focusing instead on{" "}
      administrative penalties imposed on organisations.
    </p>

  </div>
</section> */}


        {/* ================= MODULE 15 ================= */}
{/* <section className="module-section mb-5">
  <h4
    style={{
      paddingBottom: "12px",
      marginBottom: "20px",
    }}
  >
    🎯 <strong>Module 15: Exam Tips &amp; Traps</strong>
  </h4>

  <div className="mb-4 p-3 bg-white rounded shadow-sm border-start border-4 border-success">

    <h6 className="fw-bold  mb-2">
      Fresh Consent Required:
    </h6>
    <p className="mb-3">
      A new purpose for data processing always requires{" "}
      fresh consent from the Data Principal.{" "}
      Previous consent for other purposes{" "}
      cannot be repurposed.
    </p>

    <h6 className="fw-bold  mb-2">
      Anonymised Data:
    </h6>
    <p className="mb-3">
     Anonymised data falls completely{" "}
      outside the scope of the DPDP Act and is therefore{" "}
      not subject to its requirements.
    </p>

    <h6 className="fw-bold  mb-2">
      DPB Nature:
    </h6>
    <p className="mb-3">
      The Data Protection Board (DPB) functions as an{" "}
      administrative body, not a court of law, and its
      enforcement actions are administrative in nature.
    </p>

    <h6 className="fw-bold  mb-2">
      Penalties:
    </h6>
    <p className="mb-0">
      Finally, penalties under the Act are{" "}
      administrative and monetary—they do{" "}
     not include criminal imprisonment.
    </p>

  </div>
</section> */}

{/* <section className="module-section mb-5">
          <h4
            style={{
              paddingBottom: "12px",
              marginBottom: "20px",
            }}
          >
            📚 <strong>Summary: Key DPDP Principles</strong>
          </h4>

          <div className="mb-4 p-4 bg-gradient" 
               style={{
                 background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                 border: "2px solid #1e3a8a",
                 borderRadius: "16px",
                 boxShadow: "0 8px 25px rgba(30,58,138,0.15)",
               }}>
            
            <div className="mb-4">
              <h6 className="fw-bold  mb-3" style={{fontSize: "1.1rem"}}>
                🎯 Overview of DPDP Act 2023
              </h6>
              <p className="mb-0">
                India's comprehensive digital personal data protection framework balances individual rights with organisational flexibility. Built on principles of lawful, fair, transparent processing with strong emphasis on consent, rights, and accountability.[file:1]
              </p>
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <h6 className="fw-bold  mb-2">Core Principles</h6>
                <ul className="mb-0" style={{fontSize: "0.9rem"}}>
                  <li><strong>Consent-Based:</strong> Free, informed, specific, granular consent required</li>
                  <li><strong>Rights-Focused:</strong> Access, correction, erasure, grievance redressal rights</li>
                  <li><strong>Data Protection:</strong> Minimisation, accuracy, security, breach notification</li>
                </ul>
              </div>
              <div className="col-md-6">
                <h6 className="fw-bold  mb-2">Key Features</h6>
                <ul className="mb-0" style={{fontSize: "0.9rem"}}>
                  <li><strong>Individual-Centric:</strong> Data Principals' rights paramount</li>
                  <li><strong>Administrative Enforcement:</strong> Penalties by DPB, no criminal liability</li>
                  <li><strong>Technology-Neutral:</strong> Focuses on purpose, not technology type</li>
                </ul>
              </div>
            </div>

            <div className="mt-4 pt-3 border-top" style={{borderColor: "#cbd5e1"}}>
              <h6 className="fw-bold  mb-2">✅ Exam Success Tip</h6>
              <p className="mb-0 small">
                Master consent requirements, Data Principal rights, Significant Data Fiduciary obligations, and DPB enforcement mechanisms for exam readiness.[file:1]
              </p>
            </div>
          </div>
        </section> */}




      {/* </div>
    </div> */}

    {/* Progress Indicator */}
    {/* <div className="text-center mb-4 p-3 bg-light rounded"
    style={{
      marginTop:"-10px"
    }}>
      📖 Scroll through <strong>complete DPDP Act 2023 training</strong> above
    </div> */}

    {/* Control Buttons */}
{/*     
  </div>
)} */}



        </Card.Body>
      </Card>
    </Col>
  </Row>
)}


            {/* QUESTIONS & RESULTS */}
            {currentStep !== 0 && (
              <Row className="justify-content-center">
                <Col xs={12}>
                  <h4 className="mb-3">DPDP Act 2023 – Exam Practice</h4>
                  
                     {!showResults && (
                       <div className="result-action1">
                        <Button variant="primary" onClick={handleBackToIntro} style={btnPrimary}>
                      Back to Training
                       </Button>

                       </div>
                     )}
                     <Card className="iam-card">

                    <Card.Body>

                        
                      {!showResults && (
                        <div className="assessment-header-new mb-4">
                          <div className="question-chip-inline">
                            Question <strong>{currentQuestionIndex + 1}</strong> of <strong>{totalQuestions}</strong>
                          </div>
                          <div className="answered-badge-inline">Answered {answeredQuestions}/{totalQuestions}</div>
                        </div>
                      )}

                      {!showResults && currentQuestion && (
                        <>
                          <Row className="mb-4">
                            <Col xs={12} lg={7}>
                              <div className="scenario-box">
                                <span className="scenario-label">Question</span>
                                <h5 className="scenario-title">{currentQuestion.question}</h5>
                              </div>
                            </Col>
                          </Row>

                          <Row className="mb-4">
                            {currentQuestion.options.map((opt) => {
                              const isSelected = selectedAnswers[currentQuestionIndex] === opt.value;
                              return (
                                <Col xs={12} key={opt.value} className="mb-3">
                                  <div className={`option-tile ${isSelected ? 'active' : ''}`} onClick={() => handleAnswerSelect(opt.value)}>
                                    <div className="option-key">{opt.value}</div>
                                    <div className="option-content">{opt.label}</div>
                                  </div>
                                </Col>
                              );
                            })}
                          </Row>

                          {/* <Row className="mt-4">
                            <Col xs={12}>
                              <div className="d-flex flex-wrap gap-2 mb-3">
                                <button className={`buttonI ${activeTab === 'desc' ? 'active' : ''}`} onClick={() => setActiveTab('desc')}>
                                  Description
                                </button>
                                <button className={`buttonI ${activeTab === 'reco' ? 'active' : ''}`} onClick={() => setActiveTab('reco')}>
                                  Recommendations
                                </button>
                              </div>
                            </Col>
                            <Col xs={12} lg={7}>
                              {activeTab === 'desc' && (
                                <div className="content">
                                  <h6>Description</h6>
                                  <p className="mb-0">{currentQuestion.description}</p>
                                </div>
                              )}
                              {activeTab === 'reco' && (
                                <div className="content">
                                  <h6>Recommendations</h6>
                                  <p className="mb-0">{currentQuestion.recommendations}</p>
                                </div>
                              )}
                            </Col>
                          </Row> */} 

                          <Row className="mt-4">
                            <Col xs={6}>
                              <Button variant="outline-secondary" onClick={handlePrevious} disabled={currentQuestionIndex === 0}>
                                Previous
                              </Button>
                            </Col>
                            <Col xs={6} className="text-end">
                              <Button variant="primary" onClick={handleNext} style={btnPrimary}>
                                {currentQuestionIndex === totalQuestions - 1 ? 'Finish Assessment' : 'Next Question'}
                              </Button>
                            </Col>
                          </Row>
                        </>
                      )}

                      {/* RESULTS */}
<div className='result-iam-card'>
{showResults && (
  <Col xs={12}>
    <div style={{ 
      background: 'linear-gradient(135deg, #035EA5 0%, #024080 50%)', 
      borderRadius: '20px', 
      padding: '30px', 
      color: 'white',
      boxShadow: '0 25px 50px rgba(30, 58, 138, 0.4)'
    }}>
      {/* Header */} 
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          fontWeight: 'bold', 
          marginBottom: '10px',
          
        }}>
          DPDP Assessment Complete
        </h1>
        <div style={{ 
          fontSize: '1.2rem', 
          opacity: 0.9,
          marginBottom: '20px'
        }}>
          Review your exam performance
        </div>
        
        {/* Level Badge */}
        <div style={{
          display: 'inline-block',
          padding: '12px 24px',
          borderRadius: '50px',
          fontSize: '1.1rem',
          fontWeight: '600',
          background: percentage >= 80 ? '#10b981' : percentage >= 60 ? '#f59e0b' : '#ef4444',
          boxShadow: '0 8px 25px rgba(0,0,0,0.3)'
        }}>
          {percentage >= 80 ? 'EXCELLENT' : percentage >= 60 ? 'GOOD JOB' : ' IMPROVEMENT NEEDED'}
        </div>
      </div>

      <Row className="g-4">
        {/* Big Score Circle */}
        <Col xs={12} md={4}>
          <div style={{
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(20px)',
            borderRadius: '50%',
            width: '200px',
            height: '200px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            border: '4px solid rgba(255,255,255,0.3)',
            boxShadow: 'inset 0 0 50px rgba(255,255,255,0.1)'
          }}>
            <div style={{
              fontSize: '3.5rem',
              fontWeight: 'bold',
              color: 'white',
              textShadow: '0 4px 12px rgba(0,0,0,0.3)'
            }}>
              {percentage}%
            </div>
            <div style={{ fontSize: '1rem', opacity: 0.9 }}>
              Final Score
            </div>
          </div>
        </Col>

        {/* Stats Grid */}
        <Col xs={12} md={8}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '15px',
            marginBottom: '25px'
          }}>
            <div style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '16px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#60a5fa' }}>
                {totalQuestions}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 , color:"gray" }}>Total Questions</div>
            </div>

            <div style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '16px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#10b981' }}>
                {score}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 , color:"gray" }}>Correct</div>
            </div>

            <div style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '16px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#f59e0b' }}>
                {answeredQuestions - score}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9 ,color:"gray" }}>Incorrect</div>
            </div>

            <div style={{
              background: '#fff',
              padding: '20px',
              borderRadius: '16px',
              textAlign: 'center',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#777777' }}>
                {answeredQuestions}
              </div>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, color:"gray" }}>Answered</div>
            </div>
          </div>
 
          {/* Performance Message */}
          <div style={{
            background: '#fff',
            padding: '25px',
            borderRadius: '16px',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255,255,255,0.2)',
            marginBottom: '25px'
          }}>
            <h4 style={{ marginBottom: '15px', fontWeight: '600' , color:"#333333" }}>
              {percentage >= 80 ? ' Outstanding Performance!' : percentage >= 60 ? ' Good Work!' : ' Keep Learning!'}
            </h4>
            <p style={{ margin: 0, lineHeight: '1.6', opacity: 0.95 , color:"gray" }}>
              {percentage >= 80 
                ? 'Excellent DPDP readiness! You understand consent, SDFs, and penalties perfectly.' 
                : percentage >= 60 
                ? 'Good foundation. Review Data Principal rights and DPB powers for exam readiness.' 
                : 'Revisit training slides on key concepts before retaking the assessment.'
              }
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <Button
              variant="primary"
              onClick={handleBackToIntro}
              style={{
                color: 'white',
                padding: '12px 24px',
                borderRadius: '12px',
                fontWeight: '600' 
              }}
            >
              Back to Training
            </Button>
            
            {/* <Button
              variant="primary"
              onClick={() => alert('Report coming soon!')}
              style={{
                background: 'rgba(255,255,255,0.9)',
                border: 'none',
                color: '#1e3a8a',
                padding: '12px 24px',
                borderRadius: '12px',
                fontWeight: '600'
              }}
            >
              📥 Download Report
            </Button> */}

    {percentage >= 75 && (  

  <Button 
    variant="success"
    onClick={handleDownloadCertificate}
    style={{
      background: '#10b981',
      border: 'none',
      color: 'white',
      padding: '12px 24px',
      borderRadius: '12px',
      fontWeight: '600'
    }}
  >
     View Certificate
  </Button>

  )}  
          </div>
  
        </Col>
      </Row>
    </div>
    
  </Col>
)}
</div>


                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            )}

           
              
          </Col>
          <Col><BackgroundFooter isSidebarCollapsed={isSidebarCollapsed} />
          </Col>
        </Col>
      </Row>
      {/* Certificate Preview Modal */}
  <Modal 
    show={showCertificateModal} 
    onHide={() => setShowCertificateModal(false)}
    size="xl"
    centered
  >
   <Modal.Header
  closeButton
  style={{
    filter: "invert(1) brightness(0)",
    opacity: 1
  }}
>


      <Modal.Title>DPDP Certificate Preview</Modal.Title>
    </Modal.Header>
   <Modal.Body style={{ padding: '20px' }}>
  {certificatePreviewUrl ? (
    <div style={{ 
      width: '100%', 
      height: '600px', 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#525659'
    }}>
      <iframe
        src={certificatePreviewUrl}
        style={{
          width: '100%',
          height: '100%',
          border: 'none',
          borderRadius: '8px'
        }}
        title="Certificate Preview"
      />
    </div>
  ) : (
    <div style={{ 
      width: '100%', 
      height: '600px', 
      display: 'flex', 
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#f8f9fa'
    }}>
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading certificate...</span>
      </Spinner>
    </div>
  )}
</Modal.Body>

   <Modal.Footer style={{ justifyContent: 'center', gap: '15px', padding: '20px' }}>
  {/* <Button 
    variant="success" 
    onClick={handleActualDownload}
    style={{ 
      minWidth: '220px',
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '600'
    }}
  >
    📥 Download Certificate as PDF
  </Button> */}

  
  <Button 
    variant="primary" 
    onClick={handleShareLinkedIn}
    style={{ 
      minWidth: '220px',
      padding: '12px 24px',
      fontSize: '16px',
      fontWeight: '600',
      backgroundColor: '#0077b5',
      borderColor: '#0077b5'
    }}
  >
    <i className="fab fa-linkedin" style={{ marginRight: '8px' }}></i>
    Download and Share Certificate
  </Button>
</Modal.Footer>

  </Modal>

  {/* LinkedIn Share Instructions Modal */}
<Modal 
  show={showLinkedInModal} 
  onHide={() => setShowLinkedInModal(false)}
  centered
  size="lg"
>
  <Modal.Header closeButton style={{ borderBottom: 'none', backgroundColor: '#f0f9ff' }}>
    <Modal.Title style={{ color: '#0077b5', fontWeight: 'bold', fontSize: '1.5rem' }}>
      <i className="fab fa-linkedin" style={{ marginRight: '12px' }}></i>
      Share on LinkedIn
    </Modal.Title>
  </Modal.Header>
  <Modal.Body style={{ padding: '30px' }}>
    <div style={{ textAlign: 'center', marginBottom: '25px' }}>
      <div style={{ 
        fontSize: '3rem', 
        marginBottom: '15px',
        animation: 'bounce 1s ease-in-out'
      }}>
        ✅
      </div>
      <h5 style={{ color: '#10b981', fontWeight: '600', marginBottom: '10px' }}>
        Certificate Downloaded as PNG!
      </h5>
      <p style={{ color: '#6b7280', fontSize: '0.95rem' }}>
        Check your Downloads folder for the certificate image
      </p>
    </div>
    
    <div style={{ 
      background: '#f8fafc', 
      padding: '20px', 
      borderRadius: '12px',
      border: '2px dashed #cbd5e1',
      marginBottom: '20px'
    }}>
      <h6 style={{ 
        fontWeight: '600', 
        marginBottom: '15px',
        color: '#1e293b',
        fontSize: '1.1rem'
      }}>
        📝 Quick Steps to Share:
      </h6>
      <ol style={{ 
  lineHeight: '2', 
  paddingLeft: '25px',
  marginBottom: '0',
  color: '#475569'
}}>
  <li>Click <strong>"Download PNG & Open LinkedIn"</strong> button below</li>
  <li>Certificate will download as PNG image</li>
  <li>LinkedIn will open <strong>automatically</strong> in a new tab</li>
  <li>In LinkedIn, click <strong>"Add media"</strong> 📎 and select downloaded PNG</li>
  <li>Caption is pre-filled In your post, plz mention @Servqual Security  in this post .- just click <strong>"Post"</strong>! 🚀</li>
</ol>

    </div>
    
    <div style={{ 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
      padding: '15px', 
      borderRadius: '10px',
      color: 'white',
      textAlign: 'center'
    }}>
      <small>
        💡 <strong>Pro Tip:</strong> PNG images look better on LinkedIn than PDFs!
      </small>
    </div>
  </Modal.Body>
 <Modal.Footer style={{ borderTop: 'none', justifyContent: 'center', padding: '20px' }}>
  <Button 
    variant="outline-secondary"
    onClick={() => setShowLinkedInModal(false)}
    style={{ minWidth: '150px', fontWeight: '600' }}
  >
    Close
  </Button>
  <Button 
    variant="primary"
    onClick={async () => {
      console.log('🚀 Starting download & LinkedIn share...');
      
      // Step 1: Download PNG
      await downloadCertificateAsPNG();
      
      // Step 2: Wait 1 second then open LinkedIn
      setTimeout(() => {
       const linkedInCaption = encodeURIComponent(
  "🎓 I'm pleased to share that I've completed the Free DPDP (Data Protection and Privacy) Certification on the SUSAN by ServQual \n\n" + " https://susan.srql.com \n\n " + " #DPDP #DataProtection #DataPrivacy #ServQual #SUSAN"
);

        
        window.open(
          `https://www.linkedin.com/feed/?shareActive=true&text=${linkedInCaption}`,
          '_blank',
          'noopener,noreferrer'
        );
        
        setShowLinkedInModal(false);
      }, 1500);
    }}
   
  >
    <i className="fab fa-linkedin" style={{ marginRight: '10px' }}></i>
    Download PNG & Share on LinkedIn
  </Button>
</Modal.Footer>


</Modal>

{/* Hidden Certificate Canvas for PNG Generation */}
{showCertificateCanvas && (
  <CertificateCanvas
    userData={userData}
    assessmentData={{ percentage, score, totalQuestions }}
    susanLogo={susanLogo}
    onRenderComplete={() => console.log('✅ Certificate rendered')}
  />
)}

 
  
    </div>
  );
};

export default DPDPAssessmentModule;
 